import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import {
  useSubscription,
  canonicalSubscriptionPlan,
  isPremiumPlanType,
} from 'shared/context/subscriptionContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Import our new components
import UsageDashboard from 'features/settings/components/panels/UsageDashboard';
import ProUpgradeCard from 'features/settings/components/panels/ProUpgradeCard';
import BillingOverview from 'features/settings/components/panels/BillingOverview';
import AskCreditsCard from 'features/settings/components/panels/AskCreditsCard';
import CurrentPlanBanner from 'features/settings/components/panels/CurrentPlanBanner';
import useBillingV2Status from 'shared/hooks/useBillingV2Status';
import MembersPage from 'features/settings/components/panels/MembersPage';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import {
  settingsCardShell,
  settingsFocusRing,
} from 'features/settings/components/settingsTheme';

/** The signed-in user's bearer token, as the page's other billing calls send it. */
const billingAuthHeaders = () => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Error banner.
 *
 * This was MUI's `<Alert severity="info">`, which was wrong twice over. The
 * app's `createTheme` in index.js sets no `palette.mode` and no dark variant,
 * so every MUI surface is pinned to MUI's LIGHT palette regardless of
 * `data-theme` — the alert painted a pale #e5f6fd block with dark blue text on
 * the near-black billing page, the only element there that did not flip. And
 * everything routed into it is a failure ("Failed to load billing data",
 * "Failed to open billing portal"), so `info` blue was under-reporting a
 * problem the customer needs to act on.
 */
function ErrorBanner({ message, onDismiss }) {
  return (
    <div
      role="alert"
      className="mb-3 flex items-start gap-2 rounded-lg border px-3 py-2.5"
      style={{
        borderColor: 'var(--feedback-error)',
        backgroundColor: 'var(--feedback-error-bg)',
      }}
    >
      <p className="min-w-0 flex-1 text-[12px] leading-snug" style={{ color: 'var(--feedback-error)' }}>
        {message}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={`-mr-0.5 shrink-0 rounded p-0.5 transition-opacity hover:opacity-70 ${settingsFocusRing}`}
        style={{ color: 'var(--feedback-error)' }}
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

/**
 * First-load placeholder for the billing panels.
 *
 * Mirrors the row rhythm of the real panels so swapping skeleton -> content
 * doesn't shift layout. Every surface here is a themed token, so it renders
 * correctly in dark mode — the previous full-screen `rgba(248,250,252,.9)`
 * overlay was a hardcoded near-white and caused a white flash on every mount.
 */
function PanelSkeleton({ rows = 4 }) {
  const bar = 'animate-pulse rounded bg-[color:var(--surface-hover)]';
  return (
    <div className={settingsCardShell} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      <div className="divide-y divide-[color:var(--border-subtle)]">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-3 py-3.5">
            <div className={`${bar} h-3 w-[140px] shrink-0`} />
            <div className={`${bar} h-1.5 flex-1 rounded-full`} />
            <div className={`${bar} h-3 w-16 shrink-0`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {/* `.animate-fade-in` (index.css) at the same 300ms as the MUI <Fade>
          this replaces. MUI is pinned to its light palette app-wide, so the
          billing page no longer imports any of it. */}
      {value === index && <div className="animate-fade-in">{children}</div>}
    </div>
  );
}

// Real data will be fetched from API endpoints

// Plans will be fetched from API endpoint /api/billing/plans

// All data will be fetched from respective API endpoints

const NewBilling = ({ variant = 'default' }) => {
  const embedded = variant === 'embedded';
  const { selectedTeam } = useTeam();
  const {
    refreshSubscription,
    loading: subscriptionLoading,
    plan: subscriptionPlan,
    billingPlan: subscriptionBillingPlan,
    status: subscriptionStatusValue,
    isActive: subscriptionIsActive,
    billingCycle: subscriptionBillingCycle,
    currentPeriodStart,
    currentPeriodEnd,
    stripeCustomerId: subscriptionCustomerId,
    stripeSubscriptionId: subscriptionSubscriptionId,
    limits: subscriptionLimits,
    accessiblePages: subscriptionAccessiblePages,
    error: subscriptionError,
    growthQuota: subscriptionGrowthQuota,
    cancelAtPeriodEnd: subscriptionCancelAtPeriodEnd,
    amount: subscriptionAmount,
    currency: subscriptionCurrency
  } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active section from URL (secondary nav drives this)
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const settingsSegment = pathSegments.length > 2 ? pathSegments[2] : 'usage';
  const sectionToIndex = { usage: 0, members: 1, billing: 2, plans: 2, teamsSection: 1, pricing: 2, config: 0 };
  const currentTab = sectionToIndex[settingsSegment] ?? 0;

  // Only the Usage (0) and Plans/Billing (2) tabs consume billing + subscription
  // data. The Members tab (1) has its own data + spinner, so we must NOT fire the
  // billing usage fetch / forced subscription refresh for it.
  const needsBillingData = currentTab === 0 || currentTab === 2;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real data state
  const [usageData, setUsageData] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);

  // The trial only exists in billing v2. The legacy payload folds Stripe's
  // 'trialing' into 'active' (BillingService.normalizeStripeStatus), so a team
  // on trial would otherwise render as an ordinary paying Pro customer.
  const {
    isTrial,
    trialEndsAt,
    reload: reloadV2Status,
  } = useBillingV2Status(selectedTeam?._id);

  // Show a skeleton ONLY on genuine first load (no data yet). Background
  // refreshes must never blank out content the user is already reading —
  // that was the other half of the "white screen" problem.
  const showUsageSkeleton = currentTab === 0 && !usageData && (loading || subscriptionLoading);

  // ---- Billing tab: paid teams get invoices, free teams get pricing ----
  //
  // `plan` reads 'free' until the subscription resolves, so rendering the gate
  // early would flash the pricing grid at a paying customer. `subscriptionData`
  // is only populated once subscriptionLoading clears, making it our "plan is
  // known" signal — until then we hold the skeleton.
  //
  // `plan` is the LIVE entitlement — it reads 'free' the moment a subscription
  // stops being active. `billingPlan` is the server's descriptive fallback: the
  // latest subscription of ANY status, including cancelled ones, so an existing
  // customer still gets invoices and a card to fix instead of a cold pitch.
  // billingService.js says so outright: "Strictly do NOT let this touch
  // effectivePlan / planConfig / limits / features / accessiblePages /
  // isActive". Driving the paid/free gate off it did exactly that — one
  // cancelled subscription from last year made the page render as Pro, with a
  // "Next payment Apr 29, 2027" the customer will never be charged.
  const effectivePlan = canonicalSubscriptionPlan(subscriptionData?.plan ?? subscriptionPlan);
  // A trial counts as "has a plan to show" independently of what the legacy
  // payload says. Without this the billing view would depend on the legacy
  // billing_record having been written for the trialing subscription, and a
  // trial that had not synced yet would be pitched the pricing grid it just
  // came from.
  const isPaidPlan = isPremiumPlanType(effectivePlan) || isTrial;

  // Enterprise terms are a signed contract, so there is nothing to self-serve:
  // no pricing grid, no plan change, no Stripe portal. Just what they are on,
  // when it ends, and their invoices. The backend derives this from the org's
  // entitlement — an enterprise team has no per-team subscription, which is
  // exactly why this page used to show them "Free".
  const isEnterpriseTeam = effectivePlan === 'enterprise';

  // Descriptive only: did this team ever pay us? Keeps "Manage billing" and
  // past receipts reachable for a lapsed customer without claiming they are
  // still on the plan.
  const priorBillingPlan = canonicalSubscriptionPlan(
    subscriptionData?.billingPlan ?? subscriptionBillingPlan,
  );
  const hasBillingHistory = isPremiumPlanType(priorBillingPlan) && !isPaidPlan;
  const planResolved = Boolean(subscriptionData) && !subscriptionLoading;
  const showPlanSkeleton = currentTab === 2 && !planResolved;

  // Paid teams reach the pricing grid deliberately, via /settings/plans.
  // Free teams have no invoices, so /settings/billing shows pricing directly.
  // Enterprise never sees it, even at /settings/plans — offering a $249 Pro
  // plan to a customer on a negotiated contract is worse than useless.
  const showPricingGrid = !isEnterpriseTeam
    && (settingsSegment === 'plans' || settingsSegment === 'pricing' || !isPaidPlan);

  // Get API base URL from environment variable
  const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.trodo.ai';
  
  // Handle Stripe redirect success/cancel
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const success = queryParams.get('success');
    const canceled = queryParams.get('canceled');
    
    if (success === 'true') {
      // Refresh subscription context to update the app-wide subscription status
      if (selectedTeam?._id && refreshSubscription) {
        refreshSubscription(selectedTeam._id);
      }
      // Clear URL parameters
      window.history.replaceState({}, document.title, location.pathname);
    } else if (canceled === 'true') {
      // Clear URL parameters
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname, selectedTeam?._id, refreshSubscription]);

  const loadBillingData = useCallback(async () => {
    if (!selectedTeam?._id) {
      console.log('No selected team, skipping billing data load');
      return;
    }

    if (subscriptionLoading) {
      console.log('Subscription still syncing, delaying billing data load');
      return;
    }

    console.log('Loading billing data for team:', selectedTeam._id);
    console.log('Using API URL:', API_URL);
    setLoading(true);
    try {
      const teamId = selectedTeam._id;

      // Signed in: the usage route requires a member of the team since Oct 2026.
      const usageResponse = await axios.get(`${API_URL}/api/billing/usage/${teamId}`, { headers: billingAuthHeaders() });

      if (usageResponse.status === 200) {
        const responseData = usageResponse.data.data;
        const resources = responseData.resources || {};

        const cleanedUsageData = {
          teamMembers: resources.teamMembers?.usage ?? responseData.teamMembers ?? 0,
          websites: resources.websites?.usage ?? responseData.websites ?? 0,
          eventCreditsConsumed: resources.eventCredits?.usage ?? 0,
          eventCreditsRemaining: resources.eventCredits?.remaining ?? 0,
          hybridUsage: responseData.hybridUsage || null
        };

        const limits = {
          teamMembers: resources.teamMembers?.limit ?? null,
          websites: resources.websites?.limit ?? null,
          eventCredits: resources.eventCredits?.limit ?? null
        };

        // Store credit metadata for reset dates
        const creditMetadata = {
          lastResetAt: responseData.lastResetAt || null,
          billingAnchorAt: responseData.billingAnchorAt || null
        };

        setUsageData({ ...cleanedUsageData, ...creditMetadata });
        setPlanData({ limits });
      }

    } catch (err) {
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?._id, API_URL, subscriptionLoading]);

  useEffect(() => {
    if (needsBillingData) loadBillingData();
  }, [loadBillingData, needsBillingData]);

  useEffect(() => {
    if (needsBillingData && selectedTeam?._id && refreshSubscription) {
      refreshSubscription(selectedTeam._id, { force: true });
    }
  }, [selectedTeam?._id, refreshSubscription, needsBillingData]);

  useEffect(() => {
    setUsageData(null);
    setPlanData(null);
  }, [selectedTeam?._id]);

  useEffect(() => {
    if (subscriptionError) {
      setError(subscriptionError);
    }
  }, [subscriptionError]);

  useEffect(() => {
    if (subscriptionLoading) {
      return;
    }

    setSubscriptionData({
      plan: subscriptionPlan,
      billingPlan: subscriptionBillingPlan,
      status: subscriptionStatusValue,
      isActive: subscriptionIsActive,
      billingCycle: subscriptionBillingCycle,
      currentPeriodStart,
      currentPeriodEnd,
      stripeCustomerId: subscriptionCustomerId,
      stripeSubscriptionId: subscriptionSubscriptionId,
      limits: subscriptionLimits || {},
      accessiblePages: subscriptionAccessiblePages || [],
      growthQuota: subscriptionGrowthQuota || null
    });
  }, [
    subscriptionLoading,
    subscriptionPlan,
    subscriptionBillingPlan,
    subscriptionStatusValue,
    subscriptionIsActive,
    subscriptionBillingCycle,
    currentPeriodStart,
    currentPeriodEnd,
    subscriptionCustomerId,
    subscriptionSubscriptionId,
    subscriptionLimits,
    subscriptionAccessiblePages,
    subscriptionGrowthQuota
  ]);

  // Real API functions
  const refreshUsageData = async () => {
    if (!selectedTeam?._id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/billing/usage/${selectedTeam._id}`, { headers: billingAuthHeaders() });
      if (response.status === 200) {
        // Extract the resources from the response and convert to simple values
        const responseData = response.data.data;
        const resources = responseData.resources || {};
        
        // Create a cleaned up version of the usage data
        const cleanedUsageData = {
          teamMembers: resources.teamMembers?.usage ?? responseData.teamMembers ?? 0,
          websites: resources.websites?.usage ?? responseData.websites ?? 0,
          eventCreditsConsumed: resources.eventCredits?.usage ?? 0,
          eventCreditsRemaining: resources.eventCredits?.remaining ?? 0,
          hybridUsage: responseData.hybridUsage || null
        };

        const limits = {
          teamMembers: resources.teamMembers?.limit ?? null,
          websites: resources.websites?.limit ?? null,
          eventCredits: resources.eventCredits?.limit ?? null
        };
        
        // Store credit metadata for reset dates
        const creditMetadata = {
          lastResetAt: responseData.lastResetAt || null,
          billingAnchorAt: responseData.billingAnchorAt || null
        };
        
        setUsageData({ ...cleanedUsageData, ...creditMetadata });
        setPlanData({ limits });
        
        // Refresh subscription context to ensure consistency
        if (selectedTeam?._id && refreshSubscription) {
          await refreshSubscription(selectedTeam._id);
        }
      } else {
        throw new Error('Failed to refresh usage data');
      }
    } catch (err) {
      setError('Failed to refresh usage data');
    } finally {
      setLoading(false);
    }
  };

  // `plans` and `billing` both resolve to this same panel (and keep the Billing
  // rail item active), so the pricing grid is just a URL away — which also makes
  // it linkable and back-button friendly.
  const teamSegment = encodeURIComponent(selectedTeam?.name || 'team');
  const handleChangePlan = () => navigate(`/${teamSegment}/settings/plans`);

  /**
   * End the trial and start billing Pro now.
   *
   * The subscription already exists on the Pro price — a trial is that same
   * subscription with trial_end in the future — so the server just moves
   * trial_end to now and Stripe invoices the card on file. The entitlement is
   * written by the webhook, not here, so we refresh rather than assume.
   */
  const handleUpgradeNow = async () => {
    setUpgradeError(null);
    setUpgradeLoading(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/billing/v2/end-trial`,
        { teamId: selectedTeam?._id },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          withCredentials: true,
        },
      );
      // Stripe's webhook writes the entitlement; give both surfaces a chance to
      // catch up rather than painting a Pro badge we have not confirmed.
      //
      // The team id, and `force` in the options object. This read
      // `refreshSubscription(true)` — the signature is (teamId, options), so
      // `true` went through as the team id: GET /billing/status/true, then
      // Team.findById('true'), then Postgres refusing 'true' as an integer.
      // The conversion had already succeeded by then, so the customer saw
      // "Internal server error" and a page still showing their trial, over a
      // Pro subscription they had just been charged for (TRO-280).
      // The entitlement is written by Stripe's webhook, not by the response we
      // just got, so one read here usually lands before it and shows the trial
      // the customer has just left. Poll until the plan moves off TRIAL.
      //
      // Bounded, and it gives up quietly: if the webhook is slow the page keeps
      // showing the trial rather than claiming a Pro badge nothing confirmed,
      // which is the same trade the single read was making. A reload catches up.
      let v2 = await reloadV2Status();
      for (let i = 0; i < 5 && v2?.plan === 'TRIAL'; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 600));
        // eslint-disable-next-line no-await-in-loop
        v2 = await reloadV2Status();
      }
      if (refreshSubscription) await refreshSubscription(selectedTeam?._id, { force: true });
    } catch (err) {
      setUpgradeError(
        err?.response?.data?.error || 'Could not start your Pro plan. Try again.',
      );
    } finally {
      setUpgradeLoading(false);
    }
  };
  const buildAuthConfig = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      withCredentials: true
    };
  };

  const resolveStripeCustomerId = () => {
    return (
      subscriptionData?.stripeCustomerId ||
      selectedTeam?.subscription?.stripeCustomerId ||
      selectedTeam?.stripeCustomerId ||
      null
    );
  };

  const handleManageSubscription = async () => {
    const customerId = resolveStripeCustomerId();

    if (!selectedTeam?._id || !customerId) {
      setError('Missing subscription details for billing portal access.');
      return;
    }

    setPortalLoading(true);
    setError(null);
    trackClientEvent('billing_portal_opened', {}, { category: 'billing' });

    try {
      const response = await axios.post(
        `${API_URL}/api/billing/portal`,
        {
          teamId: selectedTeam._id,
          stripeCustomerId: customerId,
          returnUrl: `${window.location.origin}/${encodeURIComponent(selectedTeam.name || 'team')}/settings/billing`
        },
        buildAuthConfig()
      );

      const portalUrl = response.data?.data?.url || response.data?.url;
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        throw new Error('No billing portal URL returned');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };


  return (
    <div className={embedded ? '' : 'min-h-screen bg-[color:var(--surface-base)] py-8'}>
      <div className={embedded ? 'w-full' : 'mx-auto w-full max-w-[1200px] px-4'}>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <TabPanel value={currentTab} index={0}>
          <div className="py-4">
            {showUsageSkeleton ? (
              <PanelSkeleton rows={4} />
            ) : (
              <UsageDashboard
                usageData={usageData}
                planData={planData}
                subscriptionData={subscriptionData}
                onRefresh={refreshUsageData}
              />
            )}
          </div>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <MembersPage />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <div>
            {/* Ask credits sits ABOVE the plan branch, not inside
                BillingOverview: a FREE team renders the pricing grid instead
                of that component, and a FREE team on a $2 allowance is the
                one most likely to want a top-up. Mounting here is what makes
                "add credit" reachable on every plan. */}
            <div className="mb-5">
              <AskCreditsCard />
            </div>

            {showPlanSkeleton ? (
              <PanelSkeleton rows={3} />
            ) : showPricingGrid ? (
              <>
              {/* What they are on right now, before what they could move to. */}
              <CurrentPlanBanner
                plan={effectivePlan}
                priorPlan={hasBillingHistory ? priorBillingPlan : null}
                priorStatus={subscriptionData?.status ?? subscriptionStatusValue}
                endedOn={hasBillingHistory ? currentPeriodEnd : null}
                hasStripeCustomer={Boolean(resolveStripeCustomerId())}
                onManageSubscription={handleManageSubscription}
                manageLoading={portalLoading}
              />
              {/* The only upgrade path. The legacy bucket grid that used to
                  sit under this is gone: it sold a $75/250k "Growth" plan that
                  no longer exists, advertised limits we do not honour, and
                  surfaced backend env warnings (STRIPE_GROWTH_PRODUCT_ID) to
                  customers. Worse, it was the FALLBACK: so any team the Pro
                  card declined to serve, including enterprise, dropped through
                  to a pricing page for a retired plan. */}
              <div className="mb-5">
                <ProUpgradeCard teamId={selectedTeam?._id} />
              </div>
              </>
            ) : (
              <BillingOverview
                teamId={selectedTeam?._id}
                plan={effectivePlan}
                billingPlan={priorBillingPlan}
                status={subscriptionData?.status ?? subscriptionStatusValue}
                billingCycle={subscriptionData?.billingCycle ?? subscriptionBillingCycle}
                currentPeriodEnd={subscriptionData?.currentPeriodEnd ?? currentPeriodEnd}
                cancelAtPeriodEnd={subscriptionCancelAtPeriodEnd}
                amount={subscriptionAmount}
                currency={subscriptionCurrency}
                growthQuota={subscriptionData?.growthQuota ?? subscriptionGrowthQuota}
                hasStripeCustomer={Boolean(resolveStripeCustomerId())}
                onManageSubscription={handleManageSubscription}
                manageLoading={portalLoading}
                onChangePlan={isEnterpriseTeam ? undefined : handleChangePlan}
                isEnterprise={isEnterpriseTeam}
                contractEndsAt={subscriptionData?.currentPeriodEnd ?? currentPeriodEnd}
                isTrial={isTrial}
                trialEndsAt={trialEndsAt}
                onUpgradeNow={handleUpgradeNow}
                upgradeLoading={upgradeLoading}
                upgradeError={upgradeError}
              />
            )}
          </div>
        </TabPanel>
      </div>
    </div>
  );
};

export default NewBilling;
